import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
} from 'react-native';
import {
  Card,
  Title,
  Chip,
  Button,
  Divider,
  useTheme,
  Text,
  Switch,
  List,
} from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import apiService, { 
  CocktailFilters, 
  CocktailFamily, 
  Method, 
  Glassware, 
  BaseSpirit, 
  FlavorTag, 
  OccasionTag, 
  DietaryFlag 
} from '../services/ApiService';

interface AdvancedCocktailFiltersProps {
  filters: CocktailFilters;
  onFiltersChange: (filters: CocktailFilters) => void;
  onClearFilters: () => void;
}

const AdvancedCocktailFilters: React.FC<AdvancedCocktailFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Taxonomy data
  const [families, setFamilies] = useState<CocktailFamily[]>([]);
  const [methods, setMethods] = useState<Method[]>([]);
  const [glassware, setGlassware] = useState<Glassware[]>([]);
  const [baseSpirits, setBaseSpirits] = useState<BaseSpirit[]>([]);
  const [flavorTags, setFlavorTags] = useState<FlavorTag[]>([]);
  const [occasionTags, setOccasionTags] = useState<OccasionTag[]>([]);
  const [dietaryFlags, setDietaryFlags] = useState<DietaryFlag[]>([]);

  // Load taxonomy data
  useEffect(() => {
    loadTaxonomyData();
  }, []);

  const loadTaxonomyData = async () => {
    setLoading(true);
    try {
      const [
        familiesData,
        methodsData,
        glasswareData,
        baseSpiritsData,
        flavorTagsData,
        occasionTagsData,
        dietaryFlagsData,
      ] = await Promise.all([
        apiService.getCocktailFamilies(),
        apiService.getMethods(),
        apiService.getGlassware(),
        apiService.getBaseSpirits(),
        apiService.getFlavorTags(),
        apiService.getOccasionTags(),
        apiService.getDietaryFlags(),
      ]);

      setFamilies(familiesData);
      setMethods(methodsData);
      setGlassware(glasswareData);
      setBaseSpirits(baseSpiritsData);
      setFlavorTags(flavorTagsData);
      setOccasionTags(occasionTagsData);
      setDietaryFlags(dietaryFlagsData);
    } catch (error) {
      console.error('Error loading taxonomy data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (section: string) => {
    setExpanded(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const updateFilter = (key: keyof CocktailFilters, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const toggleArrayFilter = (key: keyof CocktailFilters, value: string) => {
    const currentValues = (filters[key] as string[]) || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    updateFilter(key, newValues);
  };

  const clearAllFilters = () => {
    onClearFilters();
  };

  const getActiveFilterCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          count += value.length;
        } else {
          count += 1;
        }
      }
    });
    return count;
  };

  const renderStrengthFilter = () => (
    <Card style={styles.filterCard}>
      <Card.Content>
        <Title style={styles.sectionTitle}>Strength</Title>
        <View style={styles.chipContainer}>
          {[
            { value: 'low', label: 'Low ABV', color: '#4CAF50' },
            { value: 'standard', label: 'Standard', color: '#FF9800' },
            { value: 'strong', label: 'Strong', color: '#F44336' },
          ].map((strength) => (
            <Chip
              key={strength.value}
              selected={filters.strength_band === strength.value}
              onPress={() => updateFilter('strength_band', 
                filters.strength_band === strength.value ? undefined : strength.value
              )}
              style={[
                styles.chip,
                filters.strength_band === strength.value && { backgroundColor: strength.color }
              ]}
              textStyle={filters.strength_band === strength.value ? { color: 'white' } : {}}
            >
              {strength.label}
            </Chip>
          ))}
        </View>
      </Card.Content>
    </Card>
  );

  const renderDifficultyFilter = () => (
    <Card style={styles.filterCard}>
      <Card.Content>
        <Title style={styles.sectionTitle}>Difficulty</Title>
        <View style={styles.chipContainer}>
          {[1, 2, 3, 4, 5].map((level) => (
            <Chip
              key={level}
              selected={filters.difficulty_max === level}
              onPress={() => updateFilter('difficulty_max', 
                filters.difficulty_max === level ? undefined : level
              )}
              style={styles.chip}
            >
              {apiService.getDifficultyStars(level)} {apiService.getDifficultyText(level)}
            </Chip>
          ))}
        </View>
      </Card.Content>
    </Card>
  );

  const renderBaseSpiritFilter = () => (
    <Card style={styles.filterCard}>
      <Card.Content>
        <Title style={styles.sectionTitle}>Base Spirit</Title>
        <View style={styles.chipContainer}>
          {baseSpirits.map((spirit) => (
            <Chip
              key={spirit.spirit_type}
              selected={filters.base_spirit === spirit.spirit_type}
              onPress={() => updateFilter('base_spirit', 
                filters.base_spirit === spirit.spirit_type ? undefined : spirit.spirit_type
              )}
              style={styles.chip}
            >
              {spirit.spirit_type} ({spirit.cocktail_count})
            </Chip>
          ))}
        </View>
      </Card.Content>
    </Card>
  );

  const renderMethodFilter = () => (
    <Card style={styles.filterCard}>
      <Card.Content>
        <Title style={styles.sectionTitle}>Method</Title>
        <View style={styles.chipContainer}>
          {methods.map((method) => (
            <Chip
              key={method.name}
              selected={filters.method === method.name}
              onPress={() => updateFilter('method', 
                filters.method === method.name ? undefined : method.name
              )}
              style={styles.chip}
            >
              {method.name} ({method.cocktail_count})
            </Chip>
          ))}
        </View>
      </Card.Content>
    </Card>
  );

  const renderGlasswareFilter = () => (
    <Card style={styles.filterCard}>
      <Card.Content>
        <Title style={styles.sectionTitle}>Glassware</Title>
        <View style={styles.chipContainer}>
          {glassware.map((glass) => (
            <Chip
              key={glass.name}
              selected={filters.glassware === glass.name}
              onPress={() => updateFilter('glassware', 
                filters.glassware === glass.name ? undefined : glass.name
              )}
              style={styles.chip}
            >
              {glass.name} ({glass.cocktail_count})
            </Chip>
          ))}
        </View>
      </Card.Content>
    </Card>
  );

  const renderFlavorTagsFilter = () => (
    <Card style={styles.filterCard}>
      <Card.Content>
        <Title style={styles.sectionTitle}>Flavor Profile</Title>
        <View style={styles.chipContainer}>
          {flavorTags.slice(0, 20).map((tag) => (
            <Chip
              key={tag.name}
              selected={(filters.flavor_tags || []).includes(tag.name)}
              onPress={() => toggleArrayFilter('flavor_tags', tag.name)}
              style={styles.chip}
            >
              {tag.name} ({tag.cocktail_count})
            </Chip>
          ))}
        </View>
      </Card.Content>
    </Card>
  );

  const renderOccasionTagsFilter = () => (
    <Card style={styles.filterCard}>
      <Card.Content>
        <Title style={styles.sectionTitle}>Occasion</Title>
        <View style={styles.chipContainer}>
          {occasionTags.map((tag) => (
            <Chip
              key={tag.name}
              selected={(filters.occasion_tags || []).includes(tag.name)}
              onPress={() => toggleArrayFilter('occasion_tags', tag.name)}
              style={styles.chip}
            >
              {tag.name} ({tag.cocktail_count})
            </Chip>
          ))}
        </View>
      </Card.Content>
    </Card>
  );

  const renderDietaryFilter = () => (
    <Card style={styles.filterCard}>
      <Card.Content>
        <Title style={styles.sectionTitle}>Dietary</Title>
        <View style={styles.chipContainer}>
          {dietaryFlags.map((flag) => (
            <Chip
              key={flag.name}
              selected={(filters.dietary_flags || []).includes(flag.name)}
              onPress={() => toggleArrayFilter('dietary_flags', flag.name)}
              style={styles.chip}
            >
              {flag.name} ({flag.cocktail_count})
            </Chip>
          ))}
        </View>
      </Card.Content>
    </Card>
  );

  const renderPrepTimeFilter = () => (
    <Card style={styles.filterCard}>
      <Card.Content>
        <Title style={styles.sectionTitle}>Prep Time</Title>
        <View style={styles.chipContainer}>
          {[
            { value: 30, label: '30s' },
            { value: 60, label: '1m' },
            { value: 120, label: '2m' },
            { value: 180, label: '3m' },
            { value: 300, label: '5m' },
            { value: 600, label: '10m' }
          ].map((time) => (
            <Chip
              key={time.value}
              selected={filters.prep_time_max === time.value}
              onPress={() => updateFilter('prep_time_max', 
                filters.prep_time_max === time.value ? undefined : time.value
              )}
              style={styles.chip}
            >
              ≤{time.label}
            </Chip>
          ))}
        </View>
      </Card.Content>
    </Card>
  );

  const renderIngredientCountFilter = () => (
    <Card style={styles.filterCard}>
      <Card.Content>
        <Title style={styles.sectionTitle}>Ingredient Count</Title>
        <View style={styles.chipContainer}>
          {[3, 4, 5, 6, 7, 8].map((count) => (
            <Chip
              key={count}
              selected={filters.ingredient_count_max === count}
              onPress={() => updateFilter('ingredient_count_max', 
                filters.ingredient_count_max === count ? undefined : count
              )}
              style={styles.chip}
            >
              ≤{count} ingredients
            </Chip>
          ))}
        </View>
      </Card.Content>
    </Card>
  );

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* Header */}
      <View style={styles.header}>
        <Title style={styles.title}>Advanced Filters</Title>
        <View style={styles.headerActions}>
          <Text style={styles.filterCount}>
            {getActiveFilterCount()} active
          </Text>
          <Button
            mode="text"
            onPress={clearAllFilters}
            disabled={getActiveFilterCount() === 0}
          >
            Clear All
          </Button>
        </View>
      </View>

      {/* Strength Filter */}
      {renderStrengthFilter()}

      {/* Difficulty Filter */}
      {renderDifficultyFilter()}

      {/* Base Spirit Filter */}
      {renderBaseSpiritFilter()}

      {/* Method Filter */}
      {renderMethodFilter()}

      {/* Glassware Filter */}
      {renderGlasswareFilter()}

      {/* Prep Time Filter */}
      {renderPrepTimeFilter()}

      {/* Ingredient Count Filter */}
      {renderIngredientCountFilter()}

      {/* Flavor Tags Filter */}
      {renderFlavorTagsFilter()}

      {/* Occasion Tags Filter */}
      {renderOccasionTagsFilter()}

      {/* Dietary Filter */}
      {renderDietaryFilter()}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  filterCount: {
    marginRight: 8,
    fontSize: 12,
    color: '#666',
  },
  filterCard: {
    marginBottom: 12,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    marginBottom: 8,
  },

});

export default AdvancedCocktailFilters;
